import Singleton from '@core/services/common/Singleton';
import { instanceGuard } from '@core/utils/services';

export interface DeviceDetails {
  uuid: string;
  // code: string;
}

export interface BetshopDetails {
  uuid: string;
  code: string;
  name: string;
  address: string;
}

export interface TerminalDetails {
  externalId: string;
  code: string;
  productId: string;
}

export interface ShopDetails {
  externalId: string;
  code: string;
  name: string;
  address: string;
  type: string;
}

export interface TerminalContext {
  kind: 'terminal';
  key: string;
  code: string;
  channel: string;
  productId: string;
}

export interface ShopContext {
  kind: 'shop';
  key: string;
  code: string;
  name: string;
  address: string;
  type: string;
}

export interface IFlag {
  name: string;
  defaultValue: any;
  description: string;
}

export type LDCallback = <T>(value: T, previous: T) => void;

export const flags: Record<string, IFlag> = {
  inPlay: {
    name: 'retail.sport.live',
    defaultValue: false,
    description: 'determines if the in-play product available',
  },
  betlerWallet: {
    name: 'retail.betler-wallet',
    defaultValue: false,
    description: 'determines if the betler wallet is being used',
  },
};

class FeatureFlagService {
  private static instance: FeatureFlagService;
  private isClientReady = false;
  private client: LDClient.LDClient;
  private context: LDClient.LDContext;

  public async init(ldClientId: string, context: LDClient.LDContext, ldReadyCallback: (value: any) => void) {
    this.client = LDClient.initialize(ldClientId, context);
    return this.client
      .waitUntilReady()
      .then((value: any) => {
        ldReadyCallback(value);
        this.isClientReady = true;
      })
      .catch((error: any) => console.error('WaitUntilReady error', error));
  }

  public setIsReady(value: boolean) {
    this.isClientReady = value;
  }

  public get isReady() {
    return this.isClientReady;
  }

  public static createInstance(): FeatureFlagService {
    if (!FeatureFlagService.instance) {
      FeatureFlagService.instance = new FeatureFlagService();
    }

    return FeatureFlagService.instance;
  }

  public static getInstance(): FeatureFlagService {
    return instanceGuard(FeatureFlagService.instance);
  }

  public flag<T>(flag: IFlag): T {
    try {
      return this.client.variation(flag.name, flag.defaultValue) as T;
    } catch (error) {
      console.error(`Failed to get ${flag.name} flag from LD switching to default ${flag.defaultValue}`, error);
      return flag.defaultValue;
    }
  }

  /**
   * Flag function wrapper for in-play flag variation.
   * @returns {boolean} Whether in-play is available or not
   */
  public isInPlayAvailable(): boolean {
    return this.flag<boolean>(flags.inPlay);
  }

  /**
   * Flag function wrapper for betler wallet integration
   * @returns {boolean} Whether betler wallet is being used or not
   */
  public isBetlerEnabled(): boolean {
    return this.flag<boolean>(flags.betlerWallet);
  }

  public async subscribeToFlagChange<T>(flag: string, callback: LDCallback, context?: LDClient.LDContext) {
    this.client.on(`change:${flag}`, callback, context);
  }

  public async unSubscribeToFlagChange<T>(flag: string, callback: LDCallback, context?: LDClient.LDContext) {
    this.client.off(`change:${flag}`, callback, context);
  }

  public async close() {
    return this.client.close();
  }

  public setNSoftCtx(
    ldClientId: string,
    device: DeviceDetails,
    // betshop: BetshopDetails,
    ldReadyCallback: (value: any) => void,
  ) {
    const deviceContext = {
      kind: 'terminal',
      key: device.uuid,
      // code: device.code,
      channel: 'LARGE_TERMINAL',
    };

    // const targetContext = {
    //     kind: 'target',
    //     key: 'SB_RO',
    // }

    // const betshopContext = {
    //   kind: 'shop',
    //   key: betshop.uuid,
    //   code: betshop.code,
    //   name: betshop.name,
    //   address: betshop.address,
    // };

    const multiContext = {
      kind: 'multi',
      terminal: deviceContext,
      //   target: targetContext,
      // shop: betshopContext,
    };

    this.context = multiContext;
    this.init(ldClientId, this.context, ldReadyCallback);
  }

  public setInHouseCtx(
    ldClientId: string,
    terminal: TerminalDetails,
    shop: ShopDetails,
    ldReadyCallback: (value: any) => void,
  ) {
    const terminalContext = {
      kind: 'terminal',
      key: terminal.externalId,
      code: terminal.code,
      channel: 'SMALL_TERMINAL',
      productId: terminal.productId,
    };

    // const targetContext = {
    //     kind: 'target',
    //     key: 'SB_RO',
    // }

    const shopContext = {
      kind: 'shop',
      key: shop.externalId,
      code: shop.code,
      name: shop.name,
      address: shop.address,
      type: shop.type,
    };

    const multiContext: LDClient.LDContext = {
      kind: 'multi',
      terminal: terminalContext,
      //   target: targetContext,
      shop: shopContext,
    };

    this.context = multiContext;
    this.init(ldClientId, this.context, ldReadyCallback);
  }
}

export default FeatureFlagService as unknown as Singleton<FeatureFlagService>;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--12!./src/terminal/core/services/flags/FeatureFlagService.ts